'use client';

import React, { useMemo } from 'react';
import { PnLTimeSeriesDataPoint } from 'src/@types/performance';
import useShowAccountValues from '../hooks/useShowAccountValues';
import {
	CartesianGrid,
	ResponsiveContainer,
	TooltipProps,
	XAxis,
	YAxis,
	Tooltip as ChartsTooltip,
	ReferenceLine,
	Bar,
	BarChart,
	Area,
	AreaChart,
} from 'recharts';
import dayjs from 'dayjs';
import currencyPipe from 'src/utils/currencyPipe';
import { cj } from 'src/utils/classNames';
import {
	calculateYAxisWidth,
	getAreaStops,
	getNegativeRedColor,
	getPositiveGreenColor,
	getTimeBasedTickFormatter,
	getTimeBasedXAxisTicks,
	getTooltipDate,
} from 'src/utils/graph';
import { BigNum } from '@drift-labs/sdk';
import { signDependentValue } from 'src/utils/signDependentValue';
import { getColorVar } from 'src/utils/theme/colorVars';
import Env from 'src/environmentVariables/EnvironmentVariables';
import Divider from 'src/components/primitives/Divider/Divider';
import WarningFilled from 'src/components/Icons/WarningFilled';
import useLatchedValue from 'src/hooks/utils/useLatchedValue';
import ChartBar from 'src/components/Charts/ChartBar';
import ChartTooltip from 'src/components/Charts/ChartTooltip';
import ChartActiveDot from 'src/components/Charts/ChartActiveDot';

type PnlChartProps = {
	data: PnLTimeSeriesDataPoint[];
	field: keyof PnLTimeSeriesDataPoint;
	positive?: boolean;
	id: string;
	label?: string;
	noGrid?: boolean;
	disableTooltips?: boolean;
	nonNotional?: boolean;
	decimalsToShow?: number;
	isInModal?: boolean;
	isPercent?: boolean;
	overrideNoBeginAtZero?: boolean;
	isHourly: boolean;
	noYearInXAxis?: boolean;
	className?: string;
	type?: 'line' | 'bar';
	showDriftLogo?: boolean;
	disableAxes?: boolean;
	/**
	 * Only applies to line chart.
	 */
	useNeutralColor?: boolean;
	isLoading?: boolean;
};

const PnlChart = (props: PnlChartProps) => {
	const NEUTRAL_AREA_COLORS_ID = `neutral-area-colors_${props.id}`;
	const SPLIT_AREA_COLORS_ID = `split-area-colors_${props.id}`;
	const SPLIT_LINE_COLORS_ID = `split-line-colors_${props.id}`;

	const showAccountValues = useShowAccountValues();

	const displayedData = useMemo(() => {
		if (props.data.length > 0) {
			return props.data
				.map((point) => ({
					...point,
					date: dayjs(point.date).valueOf(),
					isMockedSnapshot: !!point.isMockedSnapshot,
				}))
				.sort((a, b) => a.date - b.date);
		}

		return Array.from({ length: 10 }, (_, i) => ({
			date: dayjs().subtract(i, 'day').valueOf(),
			[props.field]: 0,
		}));
	}, [props.data, props.field]);

	const minValue = Math.min(
		...displayedData.map((point) => point[props.field] as number)
	);
	const maxValue = Math.max(
		...displayedData.map((point) => point[props.field] as number)
	);

	const isAllZeroValues = displayedData.every(
		(point) => point[props.field] === 0
	);

	const firstDateMs = displayedData[0]?.date ?? 0;
	const lastDateMs = displayedData[displayedData.length - 1]?.date ?? 0;

	// Use a latched `isHourly` when loading so that we don't show an
	// intermediary state of incorrect x-axis labels.
	const isHourly = useLatchedValue({
		value: props.isHourly,
		isLatched: !!props.isLoading,
	});

	const tickFormatterDuration = isHourly ? '2h' : '1d';

	const dividerColor = getColorVar('stroke-divider');
	const cursorColor = getColorVar('text-secondary');

	const chartType = props.type ?? 'line';

	const Chart = chartType === 'bar' ? BarChart : AreaChart;

	const formatYAxisTick = (tick: number) => {
		if (props.nonNotional) {
			if (!props.isPercent) {
				return String(tick);
			}

			if (tick < 0.01 && tick > 0) return '<0.01%';
			const rounded = Math.round(tick);
			return `${rounded}%`;
		}

		if (!showAccountValues) {
			return '∗∗∗∗';
		}

		const prefix = tick < 0 ? '-' : '';
		const absoluteTick = Math.abs(tick);

		const value =
			tick >= 1_000 || tick <= -1_000
				? BigNum.fromPrint(`${absoluteTick}`).toMillified()
				: absoluteTick.toLocaleString(Env.locale, {
						maximumFractionDigits: props.decimalsToShow ?? 2,
						minimumFractionDigits: props.decimalsToShow ?? 2,
					});

		return `${prefix}$${value}`;
	};

	const yAxisWidth = useMemo(() => {
		const formattedLabels = [
			formatYAxisTick(minValue),
			formatYAxisTick(maxValue),
		];
		return calculateYAxisWidth(formattedLabels);
	}, [minValue, maxValue, formatYAxisTick]);

	const { positiveColor, negativeColor } = (() => {
		if (props.useNeutralColor) {
			return {
				positiveColor: getColorVar('button-primary-bg-hover'),
				negativeColor: getColorVar('button-primary-bg-hover'),
			};
		}

		return {
			positiveColor: getPositiveGreenColor(),
			negativeColor: getNegativeRedColor(),
		};
	})();

	return (
		<ResponsiveContainer
			id={props?.id}
			className={cj(
				props.showDriftLogo && [
					'relative before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2 before:content-[""]',
					'before:h-12 before:w-35 before:bg-contain before:bg-center before:bg-no-repeat',
					'before:bg-[url("/assets/icons/chart-watermark-light.svg")] dark:before:bg-[url("/assets/icons/chart-watermark-dark.svg")]',
				],
				props.className
			)}
			width="100%"
			height="100%"
		>
			<Chart
				data={displayedData}
				/* @ts-ignore */
				overflow={chartType === 'bar' ? 'hidden' : 'visible'}
				margin={{ top: 12 }}
			>
				{!props.noGrid && (
					<CartesianGrid stroke={dividerColor} vertical={false} />
				)}

				<XAxis
					type="number"
					dataKey="date"
					domain={['dataMin', 'dataMax']}
					display={props.disableAxes ? 'none' : 'block'}
					stroke={dividerColor}
					padding={chartType === 'bar' ? { left: 40, right: 40 } : undefined}
					tickMargin={8}
					ticks={getTimeBasedXAxisTicks(
						firstDateMs,
						lastDateMs,
						tickFormatterDuration
					)}
					interval={'equidistantPreserveStart'}
					tickFormatter={getTimeBasedTickFormatter(props.noYearInXAxis)}
				/>

				<YAxis
					width={props.disableAxes ? 0 : yAxisWidth}
					domain={['auto', 'auto']}
					stroke={dividerColor}
					display={props.disableAxes ? 'none' : 'block'}
					tickLine={false}
					tickMargin={4}
					tickFormatter={formatYAxisTick}
				/>

				{/* These must stay directly rendered or else the entire chart will break when `line`. */}
				<defs>
					<linearGradient
						id={NEUTRAL_AREA_COLORS_ID}
						x1="0"
						y1="0"
						x2="0"
						y2="1"
					>
						<stop offset="0%" stopColor={positiveColor} stopOpacity={0.3} />
						<stop offset="100%" stopColor={negativeColor} stopOpacity={0} />
					</linearGradient>
				</defs>

				<defs>
					<linearGradient id={SPLIT_LINE_COLORS_ID} x1="0" y1="0" x2="0" y2="1">
						{getAreaStops(minValue, maxValue)}
					</linearGradient>
				</defs>

				<defs>
					<linearGradient id={SPLIT_AREA_COLORS_ID} x1="0" y1="0" x2="0" y2="1">
						{getAreaStops(minValue, maxValue, {
							startOpacity: 0.6,
							endOpacity: 0.2,
						})}
					</linearGradient>
				</defs>

				{chartType === 'line' && (
					<Area
						type="linear"
						connectNulls
						className="z-50"
						dataKey={props.field}
						stroke={
							props.useNeutralColor
								? positiveColor
								: `url(#${SPLIT_LINE_COLORS_ID})`
						}
						fill={`url(#${
							props.useNeutralColor
								? NEUTRAL_AREA_COLORS_ID
								: SPLIT_AREA_COLORS_ID
						})`}
						strokeWidth={2}
						dot={false}
						activeDot={({ payload, ...rest }) => (
							<ChartActiveDot
								{...rest}
								value={payload[props.field]}
								isNeutralColor={props.useNeutralColor}
							/>
						)}
						isAnimationActive={false}
					/>
				)}

				{chartType === 'bar' && (
					<Bar
						dataKey={props.field}
						className="z-50"
						// @ts-ignore
						shape={({ payload, ...rest }) => (
							<ChartBar {...rest} value={payload[props.field]} />
						)}
					/>
				)}

				{chartType === 'bar' && <ReferenceLine y={0} stroke={cursorColor} />}

				{/* This is a workaround because the normal <Area /> component doesn't render if all values are 0. */}
				{isAllZeroValues && (
					<ReferenceLine y="0" stroke={getPositiveGreenColor()} />
				)}

				<ChartsTooltip
					content={(tooltipProps) => (
						<PnlChartTooltip
							{...tooltipProps}
							decimalsToShow={props.decimalsToShow}
							nonNotional={props.nonNotional}
							isPercent={props.isPercent}
							field={props.field}
							propsLabel={props.label}
							isHourly={isHourly}
						/>
					)}
					cursor={{
						stroke: cursorColor,
						fill: cursorColor,
						opacity: chartType === 'line' ? 1 : 0.1,
						strokeDasharray: chartType === 'line' ? 4 : 0,
						strokeWidth: chartType === 'line' ? 1 : 0,
					}}
				/>
			</Chart>
		</ResponsiveContainer>
	);
};

interface PnlChartTooltipProps
	extends
		TooltipProps<any, any>,
		Pick<
			PnlChartProps,
			'decimalsToShow' | 'nonNotional' | 'isPercent' | 'field'
		> {
	propsLabel?: string;
	isHourly: boolean;
}

const PnlChartTooltip = ({
	active,
	payload,
	decimalsToShow,
	nonNotional,
	isPercent,
	field,
	propsLabel,
	isHourly,
}: PnlChartTooltipProps) => {
	if (active && payload && payload.length) {
		const data = payload[0]?.payload;

		if (!data) {
			return null;
		}

		const amount = data[field];

		const dateText = getTooltipDate(data.date, {
			isRelativeToToday: isHourly,
			dateWithTime: false,
		});

		const amountText = nonNotional
			? isPercent
				? `${String(parseFloat(amount.toFixed(decimalsToShow)))}%`
				: String(amount)
			: currencyPipe(amount, '$', decimalsToShow ?? 2);

		const amountClassName = signDependentValue(amount, {
			positive: 'text-text-positive-green-button',
			negative: 'text-text-negative-red-button',
			zero: 'text-text-default',
		});

		return (
			<ChartTooltip.Root>
				<ChartTooltip.Row>
					<span>Date</span>
					<span>
						{dateText} {!isHourly && 'GMT'}
					</span>
				</ChartTooltip.Row>

				{data.isMockedSnapshot && (
					<>
						<Divider className="bg-text-secondary" />
						<div className="flex items-start gap-x-xs">
							<WarningFilled className="text-star-yellow" size={16} />
							<span>Snapshot data is missing</span>
						</div>
						<span>Data in History is not affected</span>
					</>
				)}

				{!data.isMockedSnapshot && (
					<>
						<ChartTooltip.Row>
							<span>{propsLabel ?? field}</span>
							<span className={cj('typo-t4', amountClassName)}>
								{amountText}
							</span>
						</ChartTooltip.Row>

						{data.feesAndFunding !== undefined && (
							<ChartTooltip.Row>
								<span>Fees and Funding</span>
								<span
									className={cj(
										'typo-t4',
										signDependentValue(data.feesAndFunding, {
											positive: 'text-text-positive-green-button',
											negative: 'text-text-negative-red-button',
											zero: 'text-text-default',
										})
									)}
								>
									{currencyPipe(data.feesAndFunding, '$', decimalsToShow ?? 2)}
								</span>
							</ChartTooltip.Row>
						)}
					</>
				)}
			</ChartTooltip.Root>
		);
	}
};

export default React.memo(PnlChart);
